import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Paper, TextField, Stack } from '@mui/material';
import Loading from '../../../components/loading';
import SuccessMessage from '../../../components/successMessage';
import ErrorMessage from '../../../components/errorMessage';
import { useAnamnesis } from './hooks/useAnamnesis';
import { useSaveAnamnesis } from './hooks/useSaveAnamnesis';

export default function AnamnesisFormMobile({ patient }) {
  const navigate = useNavigate();

  const { patientId } = useParams();

  const { fetchByPatient, fetchQuestions } = useAnamnesis();
  const { save: saveAnamnesisRequest } = useSaveAnamnesis();

  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [perguntasComResposta, setPerguntasComResposta] = useState([]);
  const [queixaPrincipal, setQueixaPrincipal] = useState('');
  const [anamneseId, setAnamneseId] = useState(0);

  const [visibleQuestions, setVisibleQuestions] = useState(3);

  const obterAnamneseDoPaciente = useCallback(
    (patientId) => {
      setLoading(true);
      fetchByPatient(patientId)
        .then((resposta) => {
          if (resposta.data) {
            setAnamneseId(resposta.data.id);
            setQueixaPrincipal(resposta.data.queixaPrincipal);
            resposta.data.perguntasComRespostasDaAnamnese.forEach((pergunta) => {
              if (!pergunta.observacao) {
                pergunta.observacao = '';
              }
            });
            setPerguntasComResposta(resposta.data.perguntasComRespostasDaAnamnese);
          } else {
            obterPerguntasComRespostas();
          }
        })
        .catch((returnFromApi) => {
          if (returnFromApi.status === 401) {
            navigate('/expired-session');
          }
          console.error('ops! ocorreu um erro' + returnFromApi);
          setShowErrorMessage(true);
          setErrorMessage(returnFromApi.response?.data ?? 'Erro ao carregar anamnese.');
        })
        .finally(() => setLoading(false));
    },
    [navigate],
  );

  useEffect(() => {
    obterAnamneseDoPaciente(patientId);
  }, [patientId, obterAnamneseDoPaciente]);

  function obterPerguntasComRespostas() {
    setLoading(true);
    fetchQuestions()
      .then((resposta) => {
        setPerguntasComResposta(resposta.data);
      })
      .catch((err) => {
        console.error('ops! ocorreu um erro' + err);
      })
      .finally(() => setLoading(false));
  }
  const saveAnamnesis = (event) => {
    event.preventDefault();

    const anamnese = {
      id: anamneseId,
      queixaPrincipal: queixaPrincipal,
      perguntasComRespostasDaAnamnese: perguntasComResposta,
      pacienteId: patientId,
    };

    setLoading(true);

    saveAnamnesisRequest(anamnese)
      .then((returnFromApi) => {
        setLoading(false);
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error(returnFromApi);
      });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  function atualizarObservacao(value, perguntaComResposta) {
    const listaDePerguntasAtualizadas = [];
    perguntasComResposta.forEach((pergunta) => {
      if (pergunta.id === perguntaComResposta.id) {
        pergunta.observacao = value;
        listaDePerguntasAtualizadas.push(pergunta);
      } else {
        listaDePerguntasAtualizadas.push(pergunta);
      }
    });
    setPerguntasComResposta(listaDePerguntasAtualizadas);
  }

  return (
    <Box component="form" onSubmit={saveAnamnesis}>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <TextField
            data-cy="anamnesis-description"
            fullWidth
            label="Queixa Principal"
            value={queixaPrincipal}
            onChange={(e) => setQueixaPrincipal(e.target.value)}
            variant="outlined"
            multiline
            rows={3}
          />
        </Paper>

        {perguntasComResposta.slice(0, visibleQuestions).map((perguntaComResposta) => (
          <Paper
            data-cy="question-item"
            key={perguntaComResposta.id}
            variant="outlined"
            sx={{ p: 2, borderRadius: 2 }}
          >
            <TextField
              fullWidth
              label={perguntaComResposta.pergunta}
              value={perguntaComResposta.observacao || ''}
              onChange={(e) => atualizarObservacao(e.target.value, perguntaComResposta)}
              variant="outlined"
              multiline
              rows={2}
            />
          </Paper>
        ))}

        {visibleQuestions < perguntasComResposta.length && (
          <Button
            data-cy="show-more-questions-btn"
            variant="outlined"
            onClick={() => setVisibleQuestions((prev) => prev + 4)}
          >
            Exibir mais 3 perguntas
          </Button>
        )}

        <Button
          data-cy="save-anamnesis-btn"
          type="submit"
          variant="contained"
          fullWidth
          sx={{ p: 1.5 }}
        >
          Salvar
        </Button>
      </Stack>
    </Box>
  );
}
